import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { PaymentService } from '../payment.service';
import { PaymentStatus } from '../../stock/stock.type';
import { GetManualContractPayablesRequest, ManualContractPayable } from '../payment.type';
import { AuthService } from '../../auth/auth.service';
import { Viewport } from '../../shared/viewport';
import { SupplierInfoDialog } from '../../supplier/supplier-info-dialog/supplier-info-dialog';
import { EditFeeDialog } from '../../stock/edit-fee-dialog/edit-fee-dialog';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog';
import { PaymentState } from '../payment.state';
import { paymentLabel, statusClass } from '../payment-format';

/**
 * The Payments "Manual contracts" tab — the buy_contracts analogue of transfer
 * runs. Only finished contracts are listed: an Active one is still accruing
 * sold cards, so what it owes isn't final.
 */
@Component({
  selector: 'app-payment-contracts',
  imports: [
    CommonModule, FormsModule, MatTableModule, MatButtonModule, MatIconModule, MatFormFieldModule,
    MatSelectModule, MatCheckboxModule, MatProgressSpinnerModule, MatTooltipModule, MatPaginatorModule,
  ],
  templateUrl: './payment-contracts.html',
  styleUrl: './payment-contracts.scss',
})
export class PaymentContracts implements OnInit {
  readonly state = inject(PaymentState);
  private paymentService = inject(PaymentService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  readonly isMobile$ = inject(Viewport).isMobile$;

  readonly paymentClass = statusClass;
  readonly paymentLabel = paymentLabel;
  readonly pageSizeOptions = [25, 50, 100, 200];
  /** The most the API returns per page — used when fetching every unpaid contract. */
  private readonly maxPageSize = 200;

  readonly columns = ['select', 'date', 'supplier', 'name', 'coins', 'fee', 'payable', 'payment'];

  contracts: ManualContractPayable[] = [];
  total = 0;
  pageIndex = 0;
  pageSize = 50;
  loading = false;
  error: string | null = null;

  /** Unpaid by default — this is the list an admin works through to pay. */
  paymentStatus = 'NotPayed';
  /** Contracts that haven't sold anything yet are hidden by default, same as 0-coin runs. */
  includeZeroCoins = false;

  /** Set while every unpaid contract of the supplier is being fetched for select-all. */
  selectingAll = false;
  /** Number of contracts picked by "select all unpaid", or null while the selection is manual. */
  allUnpaidCount: number | null = null;

  get canEdit(): boolean {
    return this.authService.canWrite('suppliers_payments');
  }

  ngOnInit(): void {
    this.load();
    this.state.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => { this.pageIndex = 0; this.allUnpaidCount = null; this.load(); });
  }

  private baseRequest(): GetManualContractPayablesRequest {
    return {
      // Contracts have no date range: it filters runs by confirmation day.
      supplierId: this.state.supplierId || undefined,
      includeZeroCoins: this.includeZeroCoins,
    };
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.paymentService.getManualContractPayables({
      ...this.baseRequest(),
      paymentStatus: this.paymentStatus || undefined,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    }).subscribe({
      next: res => {
        this.contracts = res.contracts;
        this.total = res.total;
        this.loading = false;
      },
      error: err => { this.error = err?.error?.error ?? 'Failed to load manual contracts'; this.loading = false; },
    });
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.load();
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.load();
  }

  // ── Selection ──

  /** Only unpaid contracts are selectable — settling a paid one would be a no-op. */
  get selectable(): ManualContractPayable[] {
    return this.contracts.filter(c => c.payment_status !== 'Payed');
  }

  get selectedOnPage(): number {
    return this.selectable.filter(c => this.state.contracts.has(c.id)).length;
  }

  get pageAllSelected(): boolean {
    return this.selectable.length > 0 && this.selectedOnPage === this.selectable.length;
  }

  get pageSomeSelected(): boolean {
    return this.selectedOnPage > 0 && !this.pageAllSelected;
  }

  /** Select-all needs one supplier in view — a settlement can't span several. */
  get canSelectAll(): boolean {
    return !!this.state.supplierId && this.selectable.length > 0;
  }

  /** More unpaid contracts may exist beyond this page. */
  get canSelectAllPages(): boolean {
    return this.pageAllSelected && this.allUnpaidCount === null
      && (this.paymentStatus !== 'NotPayed' || this.total > this.selectable.length);
  }

  private select(c: ManualContractPayable): void {
    this.state.contracts.set(c.id, { supplierId: c.supplier_id, amount: c.payable_amount });
  }

  toggle(c: ManualContractPayable): void {
    this.allUnpaidCount = null;
    if (this.state.contracts.has(c.id)) this.state.contracts.delete(c.id);
    else this.select(c);
  }

  togglePage(): void {
    this.allUnpaidCount = null;
    if (this.pageAllSelected) {
      for (const c of this.selectable) this.state.contracts.delete(c.id);
    } else {
      for (const c of this.selectable) this.select(c);
    }
  }

  /** Selects every unpaid contract of the filtered supplier, across all pages. */
  selectAllUnpaid(): void {
    this.selectingAll = true;
    this.fetchAllUnpaid().subscribe({
      next: all => {
        for (const c of all) this.select(c);
        this.allUnpaidCount = all.length;
        this.selectingAll = false;
      },
      error: err => {
        this.selectingAll = false;
        this.snackBar.open(err?.error?.error ?? 'Failed to select all unpaid contracts', 'Close', { duration: 6000 });
      },
    });
  }

  private fetchAllUnpaid(): Observable<ManualContractPayable[]> {
    const request = { ...this.baseRequest(), paymentStatus: 'NotPayed', pageSize: this.maxPageSize };
    return this.paymentService.getManualContractPayables({ ...request, pageIndex: 0 }).pipe(
      switchMap(first => {
        if (first.totalPages <= 1) return of(first.contracts);
        const rest = Array.from({ length: first.totalPages - 1 }, (_, i) =>
          this.paymentService.getManualContractPayables({ ...request, pageIndex: i + 1 }));
        return forkJoin(rest).pipe(map(pages => [first.contracts, ...pages.map(p => p.contracts)].flat()));
      }),
    );
  }

  clearSelection(): void {
    this.allUnpaidCount = null;
    this.state.clearSelection();
  }

  // ── Row actions ──

  label(c: ManualContractPayable): string {
    return c.name || c.id.slice(0, 8);
  }

  openSupplierInfoDialog(supplierId: string, supplierName?: string): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(SupplierInfoDialog, {
      data: { supplierId: supplierId, supplierUsername: supplierName },
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    });
  }

  openEditFeeDialog(contract: ManualContractPayable): void {
    const ref = this.dialog.open(EditFeeDialog, {
      width: '360px',
      maxWidth: '95vw',
      data: {
        runLabel: contract.name || contract.supplier_name || contract.id.slice(0, 8),
        feeSnapshot: contract.fee,
      },
    });
    ref.afterClosed().subscribe((fee: number | null) => {
      if (fee === null || fee === undefined) return;
      this.paymentService.updateManualContractFee(contract.id, fee).subscribe({
        next: () => {
          this.snackBar.open('Fee updated', 'Close', { duration: 4000 });
          // Its payable changed, so a selection made at the old amount is void.
          this.state.contracts.delete(contract.id);
          this.allUnpaidCount = null;
          this.load();
        },
        error: err => this.snackBar.open(err?.error?.error ?? 'Failed to update fee', 'Close', { duration: 6000 }),
      });
    });
  }

  /**
   * Direct admin override, independent of the settlement flow — for
   * correcting a mismarked contract or writing off one paid outside it.
   * Reverting a Payed contract detaches it from whatever settlement it
   * belonged to.
   */
  togglePaymentStatus(contract: ManualContractPayable): void {
    const next: PaymentStatus = contract.payment_status === 'Payed' ? 'NotPayed' : 'Payed';
    const label = this.label(contract);
    const message = next === 'Payed'
      ? `Mark contract "${label}" as paid? This bypasses the settlement flow.`
      : `Mark contract "${label}" as unpaid? If it was part of a settlement, it will be detached from it.`;
    this.dialog.open(ConfirmDialog, {
      data: { title: 'Change payment status', message, confirmText: next === 'Payed' ? 'Mark paid' : 'Mark unpaid' },
    }).afterClosed().subscribe(ok => {
      if (!ok) return;
      this.paymentService.updateManualContractPaymentStatus(contract.id, next).subscribe({
        next: () => {
          this.snackBar.open('Payment status updated', 'Close', { duration: 4000 });
          this.state.contracts.delete(contract.id);
          this.allUnpaidCount = null;
          this.load();
        },
        error: err => this.snackBar.open(err?.error?.error ?? 'Failed to update payment status', 'Close', { duration: 6000 }),
      });
    });
  }
}
